export const COLORS = ['#fafafa','#dc3e42','#8acb65','#ee8db2','#f8d74b','#267b4b','#9252b0','#966343','#2853a2','#f29635'];
export const WIDTH = 30, HEIGHT = 21;
export const clone = value => JSON.parse(JSON.stringify(value));
export const id = () => `r${Date.now().toString(36)}${Math.random().toString(36).slice(2,8)}`;
export const createSegment = () => ({id:id(), text:'', type:'explanation', operation:'none', editorMode:'text', chart:false, grid:true, setup:[], answer:[], incorrectAnswers:[], choices:['',''], correctIndex:0, expected:1, audio:'', demo:[]});
export const createActivity = () => ({schemaVersion:1, problems:[{...createSegment(),text:'Make ten using two rods.',operation:'addition'}]});
export function dimensions(rod) { return rod.vertical ? [1,rod.length] : [rod.length,1]; }
export function place(rod,x,y) {
 const [w,h]=dimensions(rod);
 return {...rod,x:Math.max(0,Math.min(WIDTH-w,Math.round(x))),y:Math.max(0,Math.min(HEIGHT-h,Math.round(y)))};
}
export function validRod(r, {columns = WIDTH} = {}) { if(!r||typeof r!=='object')return false; const [w,h]=dimensions(r); return Number.isInteger(r.length)&&r.length>=1&&r.length<=10&&Number.isInteger(r.x)&&Number.isInteger(r.y)&&r.x>=0&&r.y>=0&&r.x+w<=columns&&r.y+h<=HEIGHT&&typeof r.vertical==='boolean'; }
const overlaps = (a,b) => {
 const [aw,ah]=dimensions(a),[bw,bh]=dimensions(b);
 return a.x<b.x+bw&&a.x+aw>b.x&&a.y<b.y+bh&&a.y+ah>b.y;
};
const hasOverlap = rods => rods.some((a,i)=>rods.slice(i+1).some(b=>overlaps(a,b)));

// A rod belongs to the column containing its entire rectangle, regardless of rotation.
export function chartColumn(rod) {
 if(!validRod(rod))return null;
 const [width]=dimensions(rod);
 return rod.x+width<=WIDTH/2?'tens':rod.x>=WIDTH/2?'ones':null;
}
export function chartTotal(rods) {
 if(!Array.isArray(rods)||rods.length>100||rods.some(r=>!validRod(r))||hasOverlap(rods))return null;
 let tens=0,ones=0;
 for(const rod of rods) {
  const column=chartColumn(rod);
  if(column==='tens'&&rod.length===10)tens++;
  else if(column==='ones'&&rod.length===1)ones++;
  else return null;
 }
 return tens<=9&&ones<=9?tens*10+ones:null;
}

// Rejected moves leave the original rod and every other authored rod untouched.
// Callers set vertical:true for a newly dispensed chart rod; later rotations persist.
export function chartPlacement(rod,x,y,others=[]) {
 const reject=reason=>({ok:false,rod,reason});
 if(!rod||![1,10].includes(rod.length))return reject('length');
 if(!Number.isFinite(x)||!Number.isFinite(y)||typeof rod.vertical!=='boolean'||!Array.isArray(others))return reject('invalid');
 const column=Math.round(x)<WIDTH/2?'tens':'ones';
 if((column==='tens'&&rod.length!==10)||(column==='ones'&&rod.length!==1))return reject('column');
 const [width,height]=dimensions(rod),left=column==='tens'?0:WIDTH/2,right=column==='tens'?WIDTH/2:WIDTH;
 const placed={...rod,x:Math.max(left,Math.min(right-width,Math.round(x))),y:Math.max(0,Math.min(HEIGHT-height,Math.round(y)))};
 const existing=others.filter(other=>other!==rod&&!(rod.id!=null&&other?.id===rod.id));
 if(existing.length>=100)return reject('capacity');
 if(existing.some(other=>!validRod(other)))return reject('invalid');
 if(existing.filter(other=>chartColumn(other)===column).reduce((count,other)=>count+(column==='ones'?other.length:1),0)>=9)return reject('capacity');
 if(existing.some(other=>overlaps(placed,other)))return reject('overlap');
 return {ok:true,rod:placed,reason:null};
}

// Insert new dispenser rods into a place-value chart without changing the
// existing drag/reposition contract in chartPlacement(). Nine Ones plus a new
// One exchange into a Ten; the saved document is changed only by the caller.
export function chartInsertRod(rod, x, y, others = []) {
 const invalid = reason => ({ok:false,rod,reason});
 if(!rod || ![1,10].includes(rod.length) || !Number.isFinite(x) || !Number.isFinite(y)
    || typeof rod.vertical !== 'boolean' || !Array.isArray(others))return invalid('invalid');
 const column = Math.round(x) < WIDTH/2 ? 'tens' : 'ones';
 if((column === 'tens' && rod.length !== 10) || (column === 'ones' && rod.length !== 1))return invalid('column');
 if(chartTotal(others) === null)return invalid('invalid');
 const tens = others.filter(other => chartColumn(other) === 'tens');
 const ones = others.filter(other => chartColumn(other) === 'ones');
 const placeTen = (replaceOnes = []) => {
  if(tens.length >= 9)return invalid('capacity');
  const used=new Set(tens.map(item=>item.x));
  let x=0;while(used.has(x))x++;
  return {ok:true,rod:{...rod,length:10,vertical:true,x,y:HEIGHT-10},reason:null,removedRods:replaceOnes};
 };
 if(column==='tens')return placeTen();
 if(ones.length===9)return placeTen(ones);
 const occupiedRows=new Set(ones.filter(item=>item.x===WIDTH/2).map(item=>item.y));
 let row=HEIGHT-1;while(occupiedRows.has(row))row--;
 return {ok:true,rod:{...rod,length:1,vertical:false,x:WIDTH/2,y:row},reason:null,removedRods:[]};
}

// Old activities may express three Ones using one 3-rod. Expand its occupied cells
// only in the view; the caller retains the saved source document until an edit.
export function normalizeChartRods(rods) {
 const unchanged={rods,valid:false,converted:false};
 if(!Array.isArray(rods)||rods.length>100||rods.some(r=>!validRod(r))||hasOverlap(rods))return unchanged;
 if(rods.some(r=>!(chartColumn(r)==='tens'&&r.length===10)&&!(chartColumn(r)==='ones'&&r.length<=9)))return unchanged;
 const ids=new Set(rods.map(r=>r.id).filter(value=>value!=null));
 const normalized=rods.flatMap((rod,index)=>{
  if(rod.length===10||rod.length===1)return [{...rod}];
  return Array.from({length:rod.length},(_,unit)=>{
   let unitId=`${rod.id??`chart-${index}`}:unit:${unit+1}`;
   while(ids.has(unitId))unitId+=':unit';
   ids.add(unitId);
   return {...rod,id:unitId,length:1,x:rod.x+(rod.vertical?0:unit),y:rod.y+(rod.vertical?unit:0)};
  });
 });
 if(chartTotal(normalized)===null)return unchanged;
 return {rods:normalized,valid:true,converted:normalized.length!==rods.length};
}

// Compare relationships, not the absolute position or the spacing between horizontal rows.
export function matches(actual,expected,{chart=false}={}) {
 if(chart) {
  const a=normalizeChartRods(actual),e=normalizeChartRods(expected);
  if(!a.valid||!e.valid)return false;
  actual=a.rods;expected=e.rods;
 }
 if(actual.length!==expected.length)return false;
 if(!actual.length)return true;
 if(hasOverlap(actual))return false;
 const horizontal=[...actual,...expected].every(r=>!r.vertical);
 const signature=rods=>{
  const minX=Math.min(...rods.map(r=>r.x)),minY=Math.min(...rods.map(r=>r.y));
  const rows=[...new Set(rods.map(r=>r.y))].sort((a,b)=>a-b);
  return rods.map(r=>chart?`${r.length}:${r.length===1?false:r.vertical}:${chartColumn(r)}`:`${r.length}:${r.x-minX}:${horizontal?rows.indexOf(r.y):r.y-minY}:${r.vertical}`).sort();
 };
 const a=signature(actual),e=signature(expected);return a.every((v,i)=>v===e[i]);
}
const isFeedbackStringReference = value => Number.isSafeInteger(value) && value > 0;
const hasFeedbackText = value => isFeedbackStringReference(value) || (typeof value === 'string' && Boolean(value.trim()));
const isFeedbackAudio = (value, text) => value == null || value === '' || (typeof value === 'string' && (
 (value === 'INHERITED' && isFeedbackStringReference(text)) ||
 /^(https?:\/\/|data:audio\/[a-z0-9.+-]+;base64,)/i.test(value) ||
 /^[A-Za-z0-9][A-Za-z0-9_.-]*\.(mp3|wav|m4a|ogg|webm)$/i.test(value)));
// Coaching can omit Incorrect Answer examples entirely. Old versions also
// saved an empty placeholder merely by opening the editor; ignore only those
// historical placeholders without treating incomplete NEW examples as valid.
export function validIncorrectAnswer(detail) {
 return detail === undefined || (detail && typeof detail === 'object' && !Array.isArray(detail)
  && Array.isArray(detail.rods) && detail.rods.length <= 100 && detail.rods.every(validRod)
  && (detail.specificFeedback === undefined || typeof detail.specificFeedback === 'boolean')
  && (detail.authorAdded === undefined || typeof detail.authorAdded === 'boolean')
  && (detail.feedbackText == null || typeof detail.feedbackText === 'string' || isFeedbackStringReference(detail.feedbackText))
  && isFeedbackAudio(detail.feedbackAudio, detail.feedbackText));
}
export function validIncorrectAnswers(segment) {
 if(!validIncorrectAnswer(segment.incorrectAnswer))return false;
 if(segment.editorIncorrectAnswerId !== undefined && typeof segment.editorIncorrectAnswerId !== 'string')return false;
 if(segment.incorrectAnswers === undefined)return true;
 return Array.isArray(segment.incorrectAnswers) && segment.incorrectAnswers.length <= 100
  && segment.incorrectAnswers.every(detail => detail && typeof detail.id === 'string' && detail.id.length > 0 && validIncorrectAnswer(detail))
  && new Set(segment.incorrectAnswers.map(detail=>detail.id)).size === segment.incorrectAnswers.length;
}
// Incorrect Answer examples are optional in Coaching. An example becomes
// active only after the author places rods. Keep feedback-only drafts in the
// saved document so an unfinished authoring session does not lose text, but
// do not block Preview or score those drafts as learner mistakes.
const uncommittedIncorrectDraft = detail => Boolean(detail && Array.isArray(detail.rods)
 && detail.rods.length === 0);

export function validateSegment(s) {
 if(!s || !['explanation','choice','fill','coaching'].includes(s.type))return false;
 if(s.editorMode!==undefined&&!['setup','text','view','answer','incorrect'].includes(s.editorMode))return false;
 if(!String(s.text||'').trim()&&!s.audio)return false;
 if(!Array.isArray(s.setup)||!s.setup.every(validRod))return false;
 if(s.type==='choice') {
  if(!Array.isArray(s.choices)||!s.choices.every(x=>typeof x==='string')||!Number.isInteger(s.correctIndex)||!s.choices[s.correctIndex]?.trim()||!s.choices.some((c,i)=>i!==s.correctIndex&&c.trim()))return false;
  // Specific feedback is optional for any individual M/C answer. Empty
  // feedback entries must not block Preview or saving a valid question.
  return true;
 }
 if(s.type==='fill')return Number.isInteger(s.expected)&&s.expected>=1&&s.expected<=10;
 if(s.type==='coaching') {
  if(!Array.isArray(s.answer)||!s.answer.length||!s.answer.every(validRod)||!validIncorrectAnswers(s))return false;
  const details=s.incorrectAnswers ?? (s.incorrectAnswer ? [s.incorrectAnswer] : []);
  return details.every(detail=>uncommittedIncorrectDraft(detail) || (detail.rods.length>0
   && !matches(detail.rods,s.answer,{chart:!!s.chart})
   && (hasFeedbackText(detail.feedbackText)||Boolean(detail.feedbackAudio&&detail.feedbackAudio!=='INHERITED'))));
 }
 return true;
}
// Explain the *same* validity rules used by Segment List/Preview.
// The returned text is a fallback label: Make translates it with String ID 0.
export function segmentValidationReason(s) {
 if(validateSegment(s))return '';
 if(!s || !['explanation','choice','fill','coaching'].includes(s.type))return 'Select a supported segment type.';
 if(s.editorMode!==undefined&&!['setup','text','view','answer','incorrect'].includes(s.editorMode))return 'Select a valid editor mode.';
 if(!String(s.text||'').trim()&&!s.audio)return 'Add an Explanation or Question, or attach audio.';
 if(!Array.isArray(s.setup)||!s.setup.every(validRod))return 'The Setup contains invalid or out-of-bounds rods.';
 if(s.type==='choice')return 'Enter a correct answer and at least one incorrect M/C answer.';
 if(s.type==='fill')return 'Enter a whole-number answer from 1 to 10.';
 if(s.type==='coaching') {
  if(!Array.isArray(s.answer)||!s.answer.length)return 'Create the Correct Answer arrangement using rods.';
  if(!s.answer.every(validRod))return 'The Correct Answer contains invalid or out-of-bounds rods.';
  if(!validIncorrectAnswers(s))return 'Check the saved Incorrect Answer examples and their feedback fields.';
  const details=s.incorrectAnswers??(s.incorrectAnswer?[s.incorrectAnswer]:[]);
  for(const detail of details){
   if(uncommittedIncorrectDraft(detail))continue;
   if(!detail.rods.length)return 'Add rods to the Incorrect Answer example or clear its unfinished feedback.';
   if(matches(detail.rods,s.answer,{chart:!!s.chart}))return 'The Incorrect Answer must differ from the Correct Answer.';
   if(!hasFeedbackText(detail.feedbackText)&&!(detail.feedbackAudio&&detail.feedbackAudio!=='INHERITED'))
    return 'Add Specific Feedback text or audio for the Incorrect Answer.';
  }
 }
 return 'Complete the selected segment before previewing it.';
}

export function validateDocument(d) {
 if(d?.schemaVersion!==1||!Array.isArray(d.problems)||!d.problems.length||d.problems.length>100) throw Error('Expected a Math Rods document with 1–100 segments.');
 for(const s of d.problems) {
 if(!s||typeof s.text!=='string'||!['explanation','choice','fill','coaching'].includes(s.type)||!['none','addition','subtraction','multiplication','division'].includes(s.operation))throw Error('Invalid segment.');
 if(s.editorMode!==undefined&&!['setup','text','view','answer','incorrect'].includes(s.editorMode))throw Error('Invalid editor mode.');
 if(!validIncorrectAnswers(s))throw Error('Invalid incorrect coaching answer or feedback.');
 for(const rods of [s.setup,s.answer]) if(!Array.isArray(rods)||rods.length>100||!rods.every(validRod))throw Error('Invalid rod arrangement.');
 if(!Array.isArray(s.demo)||s.demo.length>1000||s.demo.some(f=>!Number.isFinite(f.at)||f.at<0||f.at>60000||!Array.isArray(f.rods)||f.rods.length>100||!f.rods.every(validRod)))throw Error('Invalid demonstration.');
 if(!Array.isArray(s.choices)||s.choices.length>10||s.choices.some(c=>typeof c!=='string'))throw Error('Invalid choices.');
 for(const flag of ['withAudio','longAnswers','specificFeedback'])if(s[flag]!==undefined&&typeof s[flag]!=='boolean')throw Error('Invalid question setting.');
 for(const key of ['choiceDetails','mcExtraIncorrect'])if(s[key]!==undefined){
  if(!Array.isArray(s[key])||s[key].length>10||s[key].some(c=>!c||typeof c!=='object'||Array.isArray(c)))throw Error('Invalid answer details.');
  for(const detail of s[key]){
   for(const field of ['text','audio','feedbackText','feedbackAudio'])if(detail[field]!=null&&typeof detail[field]!=='string'&&!(field==='feedbackText'&&isFeedbackStringReference(detail[field])))throw Error('Invalid answer text or audio.');
   for(const field of ['audio','feedbackAudio'])if(detail[field]&&!(field==='feedbackAudio'&&detail[field]==='INHERITED'&&isFeedbackStringReference(detail.feedbackText))&&!/^(https?:\/\/|data:audio\/[a-z0-9.+-]+;base64,)/i.test(detail[field])&&!/^[A-Za-z0-9][A-Za-z0-9_.-]*\.(mp3|wav|m4a|ogg|webm)$/i.test(detail[field]))throw Error('Answer audio must use an HTTP(S) URL or a stored audio filename.');
  }
 }
 if(s.audio && !/^(https?:\/\/|blob:|data:audio\/[a-z0-9.+-]+;base64,)/i.test(s.audio) && !/^[A-Za-z0-9][A-Za-z0-9_.-]*\.(mp3|wav|m4a|ogg)$/i.test(s.audio))throw Error('Audio must use an HTTP(S) URL or a stored audio filename.');
 }
 return clone(d);
}
// Only complete, aligned, contiguous horizontal rows describe arithmetic. This
// rejects stray, overlapping or vertical rods instead of inventing an equation.
function arithmeticRows(rods) {
 if(!Array.isArray(rods)||!rods.length||rods.length>100||rods.some(r=>!validRod({...r,vertical:r?.vertical??false})||r.vertical)||hasOverlap(rods))return null;
 const rows=[...new Set(rods.map(r=>r.y))].sort((a,b)=>a-b).map(y=>rods.filter(r=>r.y===y).sort((a,b)=>a.x-b.x));
 if(rows.length<2||rows.length>3)return null;
 if(rows.some(row=>row.some((rod,index)=>index>0&&rod.x!==row[index-1].x+row[index-1].length)))return null;
 const [top,lower]=rows;
 if(top[0].x!==lower[0].x||top.at(-1).x+top.at(-1).length!==lower.at(-1).x+lower.at(-1).length)return null;
 return rows;
}
function arithmetic(rods) {
 const result=Object.create(null),rows=arithmeticRows(rods);
 if(!rows)return result;
 const [top,lower,quotientRow]=rows;
 const whole=top.length===1?top:lower.length===1?lower:null;
 const parts=whole===top?lower:top;
 if(rows.length===2&&whole&&parts.length>=2) {
  const total=whole[0].length,values=parts.map(r=>r.length);
  result.addition=`${values.join(' + ')} = ${total}`;
  if(top===whole&&parts.length===2)result.subtraction=`${total} − ${values[0]} = ${values[1]}`;
  if(values.every(value=>value===values[0]))result.multiplication=`${values[0]} × ${values.length} = ${total}`;
 }
 if(top.length===1&&lower.length>=1) {
  // The largest repeated part is the divisor; one shorter part is the remainder.
  // Either order of 3 and 4 under 7 therefore shows 7 ÷ 4 = 1 remainder 3.
  const dividend=top[0].length,divisor=Math.max(...lower.map(r=>r.length));
  const groups=lower.filter(r=>r.length===divisor),remainders=lower.filter(r=>r.length!==divisor);
  const quotient=groups.length,remainder=remainders[0]?.length??0;
  const validQuotient=!quotientRow||(quotientRow.length===1&&quotientRow[0].length===quotient&&quotientRow[0].x===top[0].x);
  if(remainders.length<=1&&dividend===divisor*quotient+remainder&&validQuotient)result.division=`${dividend} ÷ ${divisor} = ${quotient}${remainder?` remainder ${remainder}`:''}`;
 }
 return result;
}
export function availableOperations(rods) {
 const valid=arithmetic(rods);
 return ['none',...['addition','subtraction','multiplication','division'].filter(operation=>Object.hasOwn(valid,operation))];
}
export function equation(rods,operation) {
 return arithmetic(rods)[operation]??'';
}
